import { ReactElement } from 'react';
import { Divider, Typography, message } from 'antd';
import copy from 'copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from 'common/components/antd/Button/Button';
import { Space } from 'common/components/antd/Space/Space';
import { Spin } from 'common/components/antd/Spin/Spin';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { formatDateFromISO } from 'common/utils/format.utils';
import { useGetReportingPaymentsQuery } from 'domains/reporting/queries/reporting.query';
import { TReportingPaymentsResData } from 'domains/reporting/types/reporting.types';
import { SFooter } from './payments-details.modal.style';
import { ReportingPaymentsGetParams } from '../../enums/payments.enum';
import { TPaymentsTableRecord } from '../../types/payments.types';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TPaymentsTableRecord | {};
};

const ReportingPaymentDetailsModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: modalData,
  } = props;
  const [searchParams] = useSearchParams();
  const paymentInformationGUID = searchParams.get(ReportingPaymentsGetParams.ModalPaymentInformationGUID);
  const { pathname, search } = useLocation();
  const { t } = useTranslation();

  const {
  data: paymentsResult,
    isLoading: isLoadingPaymentsData,
  } = useGetReportingPaymentsQuery(
    ENV_COUNTERPARTY_GUID,
    {
      offset: 0,
      limit: 1,
      guids: [String(paymentInformationGUID)],
    },
    {
      enabled: !Object.keys(modalData).length && !!paymentInformationGUID && isOpen,
    }
  );

  const paymentsListData = (paymentsResult as TReportingPaymentsResData)?.payments?.[0] || modalData;
  const [messageApi, contextHolder] = message.useMessage();

  const openMessage = (): void => {
    messageApi.open({
      type: 'success',
      content: t('common.copied') as string,
      duration: 3,
    });
  };

  const copyLink = (): void => {
    const url = window.location.origin + pathname + search;
    copy(url);
    openMessage();
  };

  return (
    <EnhancedModal
      width={480}
      open={isOpen}
      onCancel={onCancelModalHandler}
    >
      <Spin spinning={!Object.keys(modalData).length && isLoadingPaymentsData} size="large">
        <Header>{t('reporting.paymentOperations.modal.details.title')}</Header>
        <Body>
          <Space
            size={16}
            direction="horizontal"
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>
              {t(`reporting.paymentOperations.modal.details.createdAt`)}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>
              {paymentsListData?.created_at && formatDateFromISO(paymentsListData?.created_at)}
            </Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>
              {t(`reporting.paymentOperations.modal.details.updatedAt`)}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>
              {paymentsListData?.updated_at && formatDateFromISO(paymentsListData?.updated_at)}
            </Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>
              {t(`reporting.paymentOperations.modal.details.amount`)}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>
              {paymentsListData?.amount || 0} {paymentsListData?.asset_code}
            </Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>
              {t(`reporting.paymentOperations.modal.details.fee`)}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 16 }}>
              {paymentsListData?.fee || 0} {paymentsListData?.asset_code}
            </Typography.Text>
          </Space>
          <Space
            size={16}
            direction="horizontal"
            style={{
              marginTop: 24,
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text>
              {t(`reporting.paymentOperations.modal.details.guid`)}
            </Typography.Text>
            <Typography.Text copyable>{paymentsListData?.guid || '0000-0000-0000-0000'}</Typography.Text>
          </Space>
          <Divider />
          <Space
            size={16}
            direction="horizontal"
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text style={{ fontSize: 16, fontWeight: 'bold' }}>
              {t(`reporting.paymentOperations.modal.details.creditedAmount`)}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 18, fontWeight: 'bold' }}>
              {(paymentsListData?.amount || 0) + (paymentsListData?.fee || 0)} {paymentsListData?.asset_code}
            </Typography.Text>
          </Space>
        </Body>
        <Footer showBorderTop={false}>
          <SFooter>
            <Button
              width="50%"
              size="large"
              style={{
                marginRight: 12,
              }}
              onClick={onCancelModalHandler}
            >
              {t('reporting.paymentOperations.modal.details.button.close')}
            </Button>
            {contextHolder}
            <Button width="50%" size="large" onClick={copyLink} type="primary">
              {t('reporting.paymentOperations.modal.details.button.copyLink')}
            </Button>
          </SFooter>
        </Footer>
      </Spin>
    </EnhancedModal>
  );
};

export default ReportingPaymentDetailsModal;
